<template>
  <div :class="{ 'overflow-hidden': appendToBody || autoFlip }" @click="$emit('click', $event)">
    <FormLabel v-if="label" :label="label" :error="!!error" />
    <v-select
      :id="id"
      ref="vselect"
      class="w-full"
      :style="{ '--vs-border-color': !!error ? 'rgb(var(--color-alert))' : '' }"
      :disabled="disabled"
      :model-value="modelValue"
      :options="options"
      :loading="loading"
      :clearable="clearable"
      :label="labelKey"
      :searchable="searchable"
      :selectable="selectable"
      :auto-select="autoSelect"
      :reduce="defaultReducer ? (opt) => opt.value : reduce"
      :placeholder="placeholder"
      :appendToBody="appendToBody || autoFlip"
      :calculate-position="autoFlip ? smartPosition : undefined"
      :outlined="outlined || null"
      :rounded="rounded || null"
      :elevated="elevated || null"
      :dense="dense || null"
      :multiple="multiple"
      @update:modelValue="updateValue"
      @blur="onBlur"
      @keyup="onKeyup"
      @option:selected="$emit('option:selected', $event)"
      @open="$emit('open', $event)"
    >
      <template v-slot:list-footer>
        <div
          v-if="showAddButton"
          style="bottom: -1px"
          class="flex sticky justify-center w-full cursor-pointer py-1 bg-gray-100 text-secondary hover:text-primary border-t border-solid border-gray-200"
          @click="$emit('add')"
        >
          {{ $t('add_new') }}
        </div>
        <slot v-else name="list-footer"></slot>
      </template>
      <template v-slot:selected-option="item">
        <span v-html="item.label"></span>
      </template>
      <template v-slot:option="item">
        <slot name="option" v-bind="item"><span v-html="item.label"></span></slot>
      </template>
      <template v-slot:no-options>
        <div class="py-1">{{ noOptions || 'No items available' }}</div>
      </template>
    </v-select>

    <label v-if="error || $slots['error']" class="text-12 text-alert mt-3 mb-1">{{ error }} <slot name="error"></slot></label>
  </div>
</template>

<script>
import ValidationMixin from '../mixins/ValidationMixin.js';
import FormLabel from './FormLabel.vue';

export default {
  name: 'FormSelect',
  components: { FormLabel },
  mixins: [ValidationMixin],
  emits: ['click', 'update:modelValue', 'option:selected', 'blur', 'keyup', 'add', 'open'],
  props: {
    disabled: Boolean,
    modelValue: {
      type: [String, Number, Object, Array],
    },
    autoSelect: Boolean,
    options: Array,
    clearable: Boolean,
    searchable: Boolean,
    appendToBody: Boolean,
    autoFlip: Boolean,
    reduce: Function,
    selectable: Function,
    defaultReducer: Boolean,
    label: String,
    labelKey: String,
    error: String,
    placeholder: String,
    elevated: Boolean,
    outlined: Boolean,
    rounded: Boolean,
    dense: Boolean,
    id: String,
    multiple: Boolean,
    showAddButton: Boolean,
    loading: Boolean,
    noOptions: String,
  },
  methods: {
    // Used with `autoFlip`: positions the dropdown below the toggle, but flips it
    // above when there isn't enough room below (e.g. at the bottom of a scroll area).
    smartPosition(dropdownList, component, { width, top, left }) {
      dropdownList.style.width = width;
      dropdownList.style.left = left;

      const toggleRect = component.$refs.toggle.getBoundingClientRect();
      const dropdownHeight = dropdownList.getBoundingClientRect().height;
      const spaceBelow = window.innerHeight - toggleRect.bottom;
      const scrollY = window.scrollY || window.pageYOffset;

      const shouldDropUp = spaceBelow < dropdownHeight && toggleRect.top > spaceBelow;

      if (shouldDropUp) {
        dropdownList.style.top = `${scrollY + toggleRect.top - dropdownHeight}px`;
        dropdownList.classList.add('v-select--drop-up');
        component.$el.classList.add('vs--drop-up');
      } else {
        dropdownList.style.top = top;
        dropdownList.classList.remove('v-select--drop-up');
        component.$el.classList.remove('vs--drop-up');
      }

      return () => {
        component.$el.classList.remove('vs--drop-up');
      };
    },
    updateValue(value) {
      this.eventbus.emit('input', value);
      this.$emit('update:modelValue', value);
    },
    onBlur($event) {
      this.eventbus.emit('blur', $event);
      this.$emit('blur', $event);
    },
    onKeyup($event) {
      this.eventbus.emit('keyup', $event);
      this.$emit('keyup', $event);
    },
  },
};
</script>
